import React from 'react';
import {
  Animated,
  Easing,
  ImageSourcePropType,
  useWindowDimensions,
} from 'react-native';
import {
  BannerCarouselWrapper,
  BannerImage,
  BannerPanel,
  Container,
  Card,
  CardHeader,
  BannerSlot,
  BannerPlaceholder,
  BannerText,
  LimitBlock,
  LimitLabel,
  LimitValue,
} from './styles';
import {BannerCarousel} from '../../shared/BannerCarousel';
import {useRef, useEffect} from 'react';
import {Button} from '../Forms/Buttons/Button';
import {useTheme} from 'styled-components/native';

type EmprestimoActionCardProps = {
  title?: string;
  limitLabel?: string;
  limitValue: string;
  actionLabel: string;
  onActionPress?: () => void;
  bannerText?: string;
  banners?: Array<{
    id: number | string;
    imageUrl: ImageSourcePropType;
    action?: () => void;
  }>;
  statementLabel?: string;
  onStatementPress?: () => void;
  children?: React.ReactNode;
  onRefresh?: () => void;
  refreshing?: boolean;
};

export function EmprestimoActionCard({
  limitLabel = 'Crédito pessoal',
  actionLabel,
  onActionPress,
  bannerText = 'Banners',
  banners,
  children,
  refreshing = false,
}: EmprestimoActionCardProps) {
  const spin = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(1)).current;

  const theme = useTheme();

  useEffect(() => {
    if (refreshing) {
      Animated.loop(
        Animated.timing(spin, {
          toValue: 1,
          duration: 700,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
      ).start();
    } else {
      spin.stopAnimation();
      spin.setValue(0);
    }
  }, [refreshing]);

  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1.06,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    anim.start();
    return () => anim.stop();
  }, [pulse]);

  const {width: windowWidth} = useWindowDimensions();
  const cardWidth = windowWidth - 48;
  const cardContentWidth = cardWidth;
  const carouselHeight = cardContentWidth / 3.72;
  const carouselItemWidth = Math.round(cardContentWidth * 0.92);

  return (
    <Container>
      <Card>
        <CardHeader>
          <LimitBlock>
            <LimitLabel>{limitLabel}</LimitLabel>
            <LimitValue>Simule agora</LimitValue>
          </LimitBlock>
        </CardHeader>
        <BannerSlot>
          {children ??
            (banners && banners.length > 0 ? (
              <BannerCarouselWrapper>
                <BannerCarousel
                  data={banners}
                  containerWidth={cardContentWidth}
                  itemWidth={carouselItemWidth}
                  height={carouselHeight}
                  parallaxScrollingOffset={cardContentWidth * 0.04}
                  renderItem={({item}) => (
                    <BannerPanel
                      onPress={item.action}
                      disabled={!item.action}
                      activeOpacity={item.action ? 0.7 : 1}>
                      <BannerImage source={item.imageUrl} resizeMode="cover" />
                    </BannerPanel>
                  )}
                />
              </BannerCarouselWrapper>
            ) : (
              <BannerPlaceholder>
                <BannerText>{bannerText}</BannerText>
              </BannerPlaceholder>
            ))}
        </BannerSlot>
      </Card>

      {onActionPress && (
        <Animated.View
          style={{
            transform: [{scale: pulse}],
            marginTop: 16,
            marginHorizontal: 0,
          }}>
          <Button
            title={actionLabel}
            onPress={onActionPress}
            color={theme.colors.primary}
            disabled={!onActionPress}
            activeOpacity={0.7}
          />
        </Animated.View>
      )}
    </Container>
  );
}
